import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Smartphone, Tablet, Monitor } from 'lucide-react';

/**
 * Available CSS units for dimensions
 */
const UNITS = [
  { value: 'px', label: 'px - Pixels' },
  { value: '%', label: '% - Percentage' },
  { value: 'rem', label: 'rem - Root EM' },
  { value: 'em', label: 'em - EM' },
  { value: 'vh', label: 'vh - Viewport Height' },
  { value: 'vw', label: 'vw - Viewport Width' },
];

/**
 * Compact units for inline display
 */
const COMPACT_UNITS = ['px', '%', 'rem', 'vw', 'vh'];

/**
 * Breakpoint definitions
 */
const BREAKPOINTS = [
  { id: 'base', label: 'Mobile', short: 'M', icon: Smartphone },
  { id: 'tablet', label: 'Tablet', short: 'T', icon: Tablet },
  { id: 'desktop', label: 'Desktop', short: 'D', icon: Monitor },
];

/**
 * ResponsiveDimensionField - Dimension input with breakpoint support
 *
 * Supports responsive values for different screen sizes (mobile/tablet/desktop).
 * Non-base breakpoints can inherit from base or have overrides.
 *
 * @param {Object} props
 * @param {Object} props.field - Field configuration
 * @param {string} props.field.key - Unique field identifier
 * @param {string} props.field.label - Display label
 * @param {Object} [props.field.default] - Default responsive dimension
 * @param {Object} props.value - Current value { base, tablet, desktop }
 * @param {Function} props.onChange - Callback when value changes
 * @param {boolean} [props.disabled] - Whether field is disabled
 * @param {boolean} [props.collapsible] - Whether to show as collapsible (default: true)
 * @param {boolean} [props.defaultCollapsed] - Initial collapsed state (default: false)
 * @param {boolean} [props.compact] - Use compact inline layout (default: false)
 * @param {string} [props.activeBreakpoint] - External breakpoint control (syncs with canvas preview)
 * @param {Function} [props.onBreakpointChange] - Callback when breakpoint changes (to resize canvas)
 */
export default function ResponsiveDimensionField({
  field,
  value,
  onChange,
  disabled = false,
  collapsible = true,
  defaultCollapsed = false,
  compact = false,
  activeBreakpoint: externalBreakpoint,
  onBreakpointChange,
}) {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);
  const [localBreakpoint, setLocalBreakpoint] = useState('base');

  // Use external breakpoint if provided, otherwise use local state
  const activeBreakpoint = externalBreakpoint ?? localBreakpoint;

  const handleBreakpointChange = (bp) => {
    if (onBreakpointChange) {
      onBreakpointChange(bp);  // Notify parent to resize canvas
    } else {
      setLocalBreakpoint(bp);  // Local only
    }
  };

  // Normalize to responsive format
  const normalizeValue = (val) => {
    if (typeof val === 'object' && val !== null) {
      // Already in responsive format
      if (val.base !== undefined) {
        return {
          base: val.base || { value: 'auto', unit: 'px' },
          tablet: val.tablet || null,
          desktop: val.desktop || null
        };
      }
      // Legacy format - convert
      return {
        base: {
          value: val.value ?? 'auto',
          unit: val.unit || 'px'
        },
        tablet: null,
        desktop: null
      };
    }
    return field.default || {
      base: { value: 'auto', unit: 'px' },
      tablet: null,
      desktop: null
    };
  };

  const responsiveObj = normalizeValue(value);

  // Get current dimension for active breakpoint (with fallback to base)
  const currentDimension = responsiveObj[activeBreakpoint] || responsiveObj.base || { value: 'auto', unit: 'px' };
  const isAuto = currentDimension.value === 'auto';

  const updateDimension = (updates) => {
    const newDimension = { ...currentDimension, ...updates };
    const newResponsiveObj = {
      ...responsiveObj,
      [activeBreakpoint]: newDimension
    };
    onChange(newResponsiveObj);
  };

  const clearOverride = () => {
    if (activeBreakpoint !== 'base') {
      const newResponsiveObj = {
        ...responsiveObj,
        [activeBreakpoint]: null
      };
      onChange(newResponsiveObj);
    }
  };

  const toggleAuto = () => {
    updateDimension({ value: isAuto ? 0 : 'auto' });
  };

  // Whether current breakpoint is inheriting from a lower breakpoint
  const isInheriting = activeBreakpoint !== 'base' && !responsiveObj[activeBreakpoint];

  // Compact mode: inline breakpoint pills + value + unit
  if (compact) {
    return (
      <div className="flex items-center gap-2 py-0.5">
        <label className="text-xs text-gray-500 w-16 flex-shrink-0 truncate">
          {field.label}
        </label>
        <div className="flex items-center gap-1 flex-1">
          {/* Breakpoint pills */}
          <div className="flex items-center gap-0.5 bg-gray-100 rounded p-0.5">
            {BREAKPOINTS.map((bp) => {
              const Icon = bp.icon;
              const hasOverride = bp.id !== 'base' && responsiveObj[bp.id];
              return (
                <button
                  key={bp.id}
                  type="button"
                  onClick={() => handleBreakpointChange(bp.id)}
                  disabled={disabled}
                  title={bp.label}
                  className={`relative p-1 rounded transition-colors
                    ${activeBreakpoint === bp.id
                      ? 'bg-white shadow-sm text-gray-900'
                      : 'text-gray-500 hover:text-gray-700'
                    } ${disabled ? 'opacity-50' : ''}`}
                >
                  <Icon className="w-3 h-3" />
                  {hasOverride && (
                    <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-blue-500 rounded-full" />
                  )}
                </button>
              );
            })}
          </div>
          {/* Value input */}
          <input
            type="number"
            value={isAuto ? '' : currentDimension.value}
            placeholder="auto"
            onChange={(e) => {
              const val = e.target.value.trim().toLowerCase();
              if (val === '' || val === 'auto') {
                updateDimension({ value: 'auto' });
              } else {
                const numVal = parseFloat(e.target.value);
                updateDimension({ value: isNaN(numVal) ? 0 : numVal });
              }
            }}
            disabled={disabled}
            title={`${field.label}${isInheriting ? ' (inherited from base)' : ''} (leave empty for auto)`}
            className={`w-16 px-2 py-1 text-xs text-center border rounded bg-gray-50
              ${isAuto ? 'text-blue-600 font-medium' : ''}
              ${isInheriting && !isAuto ? 'text-gray-400' : ''}
              border-gray-200 focus:border-blue-400 focus:ring-1 focus:ring-blue-400
              ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
          />
          {/* Unit selector - hidden when auto */}
          {!isAuto && (
            <select
              value={currentDimension.unit}
              onChange={(e) => updateDimension({ unit: e.target.value })}
              disabled={disabled}
              className={`w-12 px-1 py-1 text-xs border rounded bg-gray-50 border-gray-200
                focus:border-blue-400 focus:ring-1 focus:ring-blue-400
                ${isInheriting ? 'text-gray-400' : ''}`}
            >
              {COMPACT_UNITS.map((unit) => (
                <option key={unit} value={unit}>{unit}</option>
              ))}
            </select>
          )}
        </div>
      </div>
    );
  }

  const renderContent = () => (
    <div className="space-y-3">
      {/* Breakpoint selector tabs */}
      <div className="flex gap-1 p-1 bg-gray-100 rounded">
        {BREAKPOINTS.map((bp) => (
          <button
            key={bp.id}
            className={`flex-1 px-2 py-1 text-xs rounded transition-colors ${
              activeBreakpoint === bp.id
                ? 'bg-blue-500 text-white font-medium'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
            onClick={() => handleBreakpointChange(bp.id)}
            disabled={disabled}
          >
            {bp.label}
            {/* Dot indicator for overrides */}
            {responsiveObj[bp.id] && bp.id !== 'base' && (
              <span className="ml-1 inline-block w-1.5 h-1.5 bg-blue-300 rounded-full"></span>
            )}
          </button>
        ))}
      </div>

      {/* Auto checkbox */}
      <label className="flex items-center cursor-pointer">
        <input
          type="checkbox"
          checked={isAuto}
          onChange={toggleAuto}
          disabled={disabled}
          className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
        />
        <span className="ml-2 text-xs text-gray-700">Auto (no constraint)</span>
      </label>

      {/* Value input - disabled when auto */}
      {!isAuto && (
        <>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Value</label>
            <input
              type="number"
              value={currentDimension.value}
              onChange={(e) => updateDimension({ value: parseFloat(e.target.value) || 0 })}
              disabled={disabled}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
              placeholder="0"
              step="1"
            />
          </div>

          <div>
            <label className="block text-xs text-gray-600 mb-1">Unit</label>
            <select
              value={currentDimension.unit}
              onChange={(e) => updateDimension({ unit: e.target.value })}
              disabled={disabled}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
            >
              {UNITS.map((unit) => (
                <option key={unit.value} value={unit.value}>
                  {unit.label}
                </option>
              ))}
            </select>
          </div>

          {/* Preview */}
          <div className="text-xs text-gray-500 bg-gray-50 px-2 py-1 rounded">
            Preview: {currentDimension.value}{currentDimension.unit}
          </div>
        </>
      )}

      {/* Clear override button for non-base breakpoints */}
      {activeBreakpoint !== 'base' && responsiveObj[activeBreakpoint] && (
        <button
          onClick={clearOverride}
          disabled={disabled}
          className="w-full text-xs text-red-600 hover:text-red-800 bg-red-50 hover:bg-red-100 px-2 py-1 rounded transition-colors"
        >
          Clear {activeBreakpoint} override (inherit from base)
        </button>
      )}

      {/* Info text for inheritance */}
      {activeBreakpoint !== 'base' && !responsiveObj[activeBreakpoint] && (
        <div className="text-xs text-gray-500 bg-blue-50 px-2 py-1 rounded">
          Currently inheriting from Mobile (base)
        </div>
      )}
    </div>
  );

  if (!collapsible) {
    return (
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-2">
          {field.label}
        </label>
        {renderContent()}
      </div>
    );
  }

  return (
    <div className="border border-gray-200 rounded">
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 hover:bg-gray-100 transition-colors"
      >
        <label className="text-xs font-medium text-gray-700 cursor-pointer">
          {field.label}
        </label>
        {isCollapsed ? (
          <ChevronDown className="w-4 h-4 text-gray-500" />
        ) : (
          <ChevronUp className="w-4 h-4 text-gray-500" />
        )}
      </button>
      {!isCollapsed && (
        <div className="p-3">
          {renderContent()}
        </div>
      )}
    </div>
  );
}
